import { Button, Input, Spinner } from '@heroui/react';
import { Search } from 'lucide-react';
import React, { FC, useState } from 'react';

import { useHeroToast } from '../../../../hooks';
import { StockPhoto, useImportStockPhoto, useSearchStockPhotos } from '../../../../queries';

interface StockPhotoPanelProps {
  onPicked: (url: string) => void;
}

/**
 * Bank zdjęć Magnific.
 *
 * Wyszukiwanie jest darmowe i pokazuje wyłącznie podglądy z CDN dostawcy, których nie
 * wolno wstawiać do wiadomości. Dopiero POBRANIE licencjonuje plik, zabiera jedno z
 * dziennej puli i odkłada zdjęcie w naszej bibliotece, więc idzie osobnym, jawnym
 * kliknięciem, a nie od razu po wybraniu kafelka.
 */
export const StockPhotoPanel: FC<StockPhotoPanelProps> = ({ onPicked }) => {
  const { showError, showSuccess } = useHeroToast();
  const [term, setTerm] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [selected, setSelected] = useState<StockPhoto | null>(null);

  const { data, isFetching } = useSearchStockPhotos({ term: submitted }, submitted.length > 1);
  const { mutateAsync: importPhoto, isPending } = useImportStockPhoto();

  const handleImport = async () => {
    if (!selected) return;
    try {
      const result = await importPhoto({ id: selected.id, usage: 'element' });
      showSuccess(result.downloaded
        ? `Pobrano zdjęcie. Zostało ${result.downloadsLeft} pobrań na dziś.`
        : 'Zdjęcie było już w bibliotece, pula nietknięta.');
      onPicked(result.asset.url);
    } catch {
      showError('Nie udało się pobrać zdjęcia z banku.');
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(term.trim());
          setSelected(null);
        }}
      >
        <Input
          size="sm"
          label="Szukaj w banku zdjęć"
          placeholder="np. hotel lobby"
          value={term}
          onValueChange={setTerm}
        />
        <Button size="sm" type="submit" startContent={<Search className="h-4 w-4" />}>
          Szukaj
        </Button>
      </form>

      {isFetching && <Spinner size="sm" className="self-center" />}

      {!isFetching && data && data.items.length === 0 && (
        <p className="py-6 text-center text-sm text-gray-500">Brak wyników dla tej frazy.</p>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="grid max-h-72 grid-cols-4 gap-3 overflow-auto">
            {data.items.map((photo) => (
              <button
                key={photo.id}
                type="button"
                title={photo.title}
                className={`overflow-hidden rounded-lg border p-1 ${selected?.id === photo.id
                  ? 'border-gray-900'
                  : 'border-gray-200 hover:border-gray-400'}`}
                onClick={() => setSelected(photo)}
              >
                <img src={photo.previewUrl} alt="" className="h-20 w-full object-cover" />
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2">
            <span className="text-xs text-gray-500">
              {selected
                ? `Wybrano: ${selected.title || 'bez tytułu'}`
                : 'Zaznacz zdjęcie, żeby je pobrać.'}
              {` Pobrań na dziś: ${data.downloadsLeft}.`}
            </span>
            <Button size="sm" color="primary" isDisabled={!selected} isLoading={isPending} onPress={handleImport}>
              Pobierz i użyj
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
